'use client';

import React, { useState, useCallback } from 'react';
import type { Toast, ToastType } from '@/components/ui/toast';

export const useToast = () => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback(
    ({
      title,
      message,
      type = 'info',
      duration = 2000,
      icon,
    }: {
      title: string;
      message: string;
      type?: ToastType;
      duration?: number;
      icon?: React.ReactNode;
    }) => {
      const id = Math.random().toString(36).substr(2, 9);
      const newToast: Toast = {
        id,
        title,
        message,
        type,
        duration,
        icon,
      };

      setToasts((prev) => [...prev, newToast]);
      return id;
    },
    [],
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const removeAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // Convenience methods
  const showComingSoon = useCallback(
    (regionName: string) => {
      const comingSoonIcon = React.createElement('div', { className: 'relative' }, [
        React.createElement(
          'svg',
          {
            key: 'icon',
            className: 'w-5 h-5',
            fill: 'currentColor',
            viewBox: '0 0 20 20',
          },
          React.createElement('path', {
            fillRule: 'evenodd',
            d: 'M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z',
            clipRule: 'evenodd',
          }),
        ),
        React.createElement('div', {
          key: 'pulse',
          className: 'absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full animate-pulse',
        }),
      ]);

      return addToast({
        title: `${regionName} - Coming Soon!`,
        message: `We're working hard to bring our services to ${regionName}. Stay tuned for updates!`,
        type: 'warning',
        duration: 3000,
        icon: comingSoonIcon,
      });
    },
    [addToast],
  );

  const showSuccess = useCallback(
    (title: string, message: string) => {
      return addToast({
        title,
        message,
        type: 'success',
        duration: 4000,
      });
    },
    [addToast],
  );

  const showError = useCallback(
    (title: string, message: string) => {
      return addToast({
        title,
        message,
        type: 'error',
        duration: 6000,
      });
    },
    [addToast],
  );

  const showInfo = useCallback(
    (title: string, message: string) => {
      return addToast({
        title,
        message,
        type: 'info',
        duration: 2000,
      });
    },
    [addToast],
  );

  return {
    toasts,
    addToast,
    removeToast,
    removeAllToasts,
    showComingSoon,
    showSuccess,
    showError,
    showInfo,
  };
};
